import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import { PhCheckCircle, PhXCircle, PhGraduationCap } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format.ts';
import { Section } from 'tio-reimagined/components/layout';
import Chip from 'tio-reimagined/components/chip';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type AiRecommendationModel from 'tio-data/models/ai-recommendation';
import RecommendationCard from './recommendation-card.gts';

/**
 * Application Analysis View
 * Displays AI-generated analysis for the entire application based on actual API structure
 */
export interface ApplicationAnalysisViewSignature {
  Args: {
    recommendation: AiRecommendationModel;
    application: TasApplicationModel;
  };
}

interface ChargeItem {
  amount: number;
  detail: string;
  date?: string;
  provided_reimbursement_documents: boolean;
  provided_reimbursement_documents_for_associated_course: boolean;
  associated_course_name: string;
  follows_all_policies: boolean;
  is_approved_for_reimbursement: boolean;
  saw_proof_of_payment: boolean;
  proof_of_payment_file_name?: string;
}

interface CreditItem {
  amount: number;
  detail: string;
  proof_of_credit_file_name?: string;
}

interface NameMatch {
  is_name_match: boolean;
  reason: string;
}

interface CourseReimbursementChecklist {
  course_name: string;
  period: string;
  begin_date: string;
  end_date: string;
  document_submission_deadline_date: string;
  course_completion_document_name: string;
  completion_document_submission_date: string;
  completion_document_submitted_within_deadline: boolean;
  course_payment_invoice_document_name: string;
  course_payment_invoice_submission_date: string;
  course_payment_document_submitted_within_deadline: boolean;
  number_of_credits: number;
  grade: string;
  applicant_satisfied_all_requirements_for_reimbursement: boolean;
  reason: string;
}

interface EmployeeEligibility {
  is_eligible: boolean;
  reason: string;
}

interface Proration {
  total_credits_in_application: number;
  total_credits_attempted: number;
  total_credits_passed: number;
}

interface LastNamePresent {
  is_last_name_present: boolean;
  reason: string;
}

interface ReasoningStructure {
  charges?: ChargeItem[];
  credits?: CreditItem[];
  name_match?: NameMatch;
  proration?: Proration;
  last_name_present_in_all_receipts?: LastNamePresent;
  course_reimbursement_checklist?: CourseReimbursementChecklist[];
  employee_eligibility?: EmployeeEligibility;
}

interface RecommendationSummary {
  status: string;
  approved_amount: number;
  available_funds: number;
  requested_amount: number;
  tas_application_id?: string;
  calculation_details?: string;
}

interface ResponseJsonStructure {
  reasoning?: ReasoningStructure;
  recommendation_summary?: RecommendationSummary;
}

class ApplicationAnalysisView extends Component<ApplicationAnalysisViewSignature> {
  get responseData(): ResponseJsonStructure {
    return (this.args.recommendation.responseJson as ResponseJsonStructure) || {};
  }

  get recommendationSummary(): RecommendationSummary | undefined {
    return this.responseData.recommendation_summary;
  }

  get reasoning(): ReasoningStructure {
    return this.responseData.reasoning || {};
  }

  get recommendation(): string {
    return (
      this.recommendationSummary?.status || this.args.recommendation.recommendedTo || 'UNKNOWN'
    );
  }

  get charges(): ChargeItem[] {
    return this.reasoning.charges || [];
  }

  get credits(): CreditItem[] {
    return this.reasoning.credits || [];
  }

  get nameMatch(): NameMatch | undefined {
    return this.reasoning.name_match;
  }

  get courseReimbursementChecklist(): CourseReimbursementChecklist[] {
    return this.reasoning.course_reimbursement_checklist || [];
  }

  get proration(): Proration | undefined {
    return this.reasoning.proration;
  }

  get lastNamePresent(): LastNamePresent | undefined {
    return this.reasoning.last_name_present_in_all_receipts;
  }

  get employeeEligibility(): EmployeeEligibility | undefined {
    return this.reasoning.employee_eligibility;
  }

  get calculationDetails(): string | undefined {
    return this.recommendationSummary?.calculation_details;
  }

  <template>
    <div class="p-6 space-y-6">
      {{! Main Recommendation Card }}
      <div>
        <h3 class="text-base font-semibold mb-4 text-gray-800">AI Recommendation</h3>
        {{#if this.recommendationSummary}}
          <RecommendationCard
            @recommendation={{this.recommendation}}
            @calculationDetails={{this.calculationDetails}}
            @approvedAmount={{this.recommendationSummary.approved_amount}}
            @requestedAmount={{this.recommendationSummary.requested_amount}}
          />
        {{/if}}
      </div>

      {{! Employee Eligibility }}
      {{#if this.employeeEligibility}}
        <Section>
          <:header>Employee Eligibility</:header>
          <:body>
            <div class="flex items-start gap-3">
              {{#if this.employeeEligibility.is_eligible}}
                <PhCheckCircle @size="20" class="text-green-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Eligible</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="text-red-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="danger" @size="sm">Not Eligible</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Course Reimbursement Checklist }}
      {{#if this.courseReimbursementChecklist.length}}
        <Section>
          <:header>Course Reimbursement Checklist</:header>
          <:body>
            <div class="space-y-4">
              {{#each this.courseReimbursementChecklist as |course|}}
                <div class="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  {{#if course.applicant_satisfied_all_requirements_for_reimbursement}}
                    <PhCheckCircle @size="20" class="text-green-600 shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="text-red-600 shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <PhGraduationCap @size="16" class="text-gray-500" />
                      <p class="font-semibold text-sm text-gray-800">{{course.course_name}}</p>
                    </div>
                    {{#if course.grade}}
                      <p class="text-xs text-gray-500 mb-1">Grade:
                        {{course.grade}}</p>
                    {{/if}}
                    {{#if course.period}}
                      <p class="text-xs text-gray-500 mb-1">Period:
                        {{course.period}}</p>
                    {{/if}}
                    <p class="text-xs text-gray-500 mb-1">Credits:
                      {{course.number_of_credits}}</p>
                    <p class="text-sm text-gray-600 mt-2">{{course.reason}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Name Match Verification }}
      {{#if this.nameMatch}}
        <Section>
          <:header>Name Verification</:header>
          <:body>
            <div class="flex items-start gap-3">
              {{#if this.nameMatch.is_name_match}}
                <PhCheckCircle @size="20" class="text-green-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Name Verified</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="text-red-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Name Mismatch</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Last Name Present in Receipts }}
      {{#if this.lastNamePresent}}
        <Section>
          <:header>Last Name in Receipts</:header>
          <:body>
            <div class="flex items-start gap-3">
              {{#if this.lastNamePresent.is_last_name_present}}
                <PhCheckCircle @size="20" class="text-green-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Verified</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="text-red-600 shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Not Found</Chip>
                  <p class="text-sm text-gray-600 mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Proration }}
      {{#if this.proration}}
        <Section>
          <:header>Credit Proration</:header>
          <:body>
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Credits in Application</span>
                <span
                  class="font-semibold text-gray-800"
                >{{this.proration.total_credits_in_application}}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Credits Attempted</span>
                <span
                  class="font-semibold text-gray-800"
                >{{this.proration.total_credits_attempted}}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Credits Passed</span>
                <span
                  class="font-semibold text-gray-800"
                >{{this.proration.total_credits_passed}}</span>
              </div>
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Charges Breakdown }}
      {{#if this.charges.length}}
        <Section>
          <:header>Charges Analysis</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.charges as |charge|}}
                <div class="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  {{#if charge.is_approved_for_reimbursement}}
                    <PhCheckCircle @size="20" class="text-green-600 shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="text-red-600 shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="flex items-center justify-between mb-1">
                      <p class="font-semibold text-sm text-gray-800">{{formatAsDollars
                          charge.amount
                          (hash cents=false)
                        }}</p>
                      {{#if charge.is_approved_for_reimbursement}}
                        <Chip @intent="success" @size="sm">Approved</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">Not Approved</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm text-gray-600 mb-2">{{charge.detail}}</p>
                    {{#if charge.saw_proof_of_payment}}
                      <div class="flex items-center gap-1 text-xs text-gray-500">
                        <PhCheckCircle @size="14" class="text-green-600" />
                        <span>Proof of payment verified</span>
                        {{#if charge.proof_of_payment_file_name}}
                          <span class="text-gray-500">•
                            {{charge.proof_of_payment_file_name}}</span>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Credits/Discounts }}
      {{#if this.credits.length}}
        <Section>
          <:header>Credits & Discounts</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.credits as |credit|}}
                <div class="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <div class="flex-1">
                    <div class="flex items-center justify-between mb-1">
                      <p class="font-semibold text-sm text-gray-800">-{{formatAsDollars
                          credit.amount
                          (hash cents=false)
                        }}</p>
                      {{#if credit.proof_of_credit_file_name}}
                        <Chip
                          @intent="primary"
                          @size="sm"
                        >{{credit.proof_of_credit_file_name}}</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm text-gray-600">{{credit.detail}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}
    </div>
  </template>
}

export default ApplicationAnalysisView;
